import { useState } from 'react';
import { toast } from 'sonner';
import z from 'zod';

import { useResetCompanyMutation } from '@/modules/companies/mutations/useResetCompanyMutation';
import type { CompanyApi } from '@/modules/companies/types';
import { Form } from '@/modules/forms/components/Form';
import { FormInput } from '@/modules/forms/components/FormInput';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/modules/ui/components/dialog/Dialog';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

interface DemoResetModalProps {
  company: CompanyApi;
  trigger: React.ReactNode;
}

export const DemoResetModal = ({ company, trigger }: DemoResetModalProps) => {
  const [open, setOpen] = useState(false);
  const resetMutation = useResetCompanyMutation();

  const resetSchema = z.object({
    companyName: z
      .string()
      .min(1, 'Company name is required')
      .refine((val) => val.toLowerCase() === company.name.toLowerCase(), {
        message: 'Company name does not match',
      }),
  });

  type ResetFormValues = z.infer<typeof resetSchema>;

  const handleSubmit = (values: ResetFormValues) => {
    resetMutation.mutate(
      { id: company.id, companyName: values.companyName },
      {
        onSuccess: () => {
          toast.success('Demo data has been reset');
          setOpen(false);
        },
        onError: (error) => {
          toast.error(
            `Failed to reset demo data: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset company data for demo</DialogTitle>
          <DialogDescription className="space-y-3">
            <p>This action will:</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>Reset company onboarding state</li>
              <li>Remove all jobs</li>
              <li>Remove all company notes</li>
              <li>Disconnect Slack for the current user</li>
            </ul>
            <p className="text-destructive font-semibold">
              This action is irreversible and cannot be undone.
            </p>
          </DialogDescription>
        </DialogHeader>

        <Form schema={resetSchema} onSubmit={handleSubmit}>
          <div className="space-y-4">
            <FormInput
              name="companyName"
              label={`Type "${company.name}" to confirm`}
              placeholder="Enter company name"
              autoComplete="off"
              required
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={resetMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={resetMutation.isPending}
              >
                {resetMutation.isPending ? 'Resetting...' : 'Reset Demo Data'}
              </Button>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
